import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import EditIcon from "@mui/icons-material/Edit";
import RemoveIcon from "@mui/icons-material/Remove";

import Panel from "../../components/brand/Panel";
import {
  applyServiceChargeRecalculation,
  getServiceChargeRecalculationPreview,
} from "../../api/financeApi";
import { getUnitDetails, updateUnit } from "../../api/unitApi";
import { getApiErrorMessage, isConcurrencyConflict } from "../../utils/concurrency";
import {
  FINANCE_UNITS_TABLE_MAX_HEIGHT,
  unitSortKey,
  useClientTableSort,
} from "./useClientTableSort";

const money = (value) =>
  value == null
    ? "—"
    : new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value);

function DeltaCell({ delta, firstTime }) {
  if (firstTime || delta == null) {
    return <Typography variant="body2" color="text.secondary">—</Typography>;
  }
  if (delta === 0) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
        <RemoveIcon fontSize="small" />
        {money(0)}
      </Box>
    );
  }
  const up = delta > 0;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        color: up ? "error.main" : "success.main",
        fontWeight: 600,
      }}
    >
      {up ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
      {money(Math.abs(delta))}
    </Box>
  );
}

export default function ServiceChargeRecalculationPanel({
  propertyId,
  refreshKey,
  recalculationNeeded,
  recalculationReason,
  onApplied,
  onMessage,
  onError,
}) {
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadError, setLoadError] = useState("");

  const [editUnit, setEditUnit] = useState(null);
  const [editSquareFeet, setEditSquareFeet] = useState("");
  const [editRowVersion, setEditRowVersion] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");

  const unitSortAccessors = useMemo(
    () => ({
      unit_code: unitSortKey,
      square_feet: (row) => row.square_feet,
      estate_charge_amount: (row) => row.estate_charge_amount,
      block_charge_amount: (row) => row.block_charge_amount,
      current_service_charge: (row) => row.current_service_charge,
      new_service_charge: (row) => row.new_service_charge,
      delta: (row) => row.delta,
    }),
    []
  );
  const { sortedRows: sortedUnits, sortField, sortDirection, handleSort } = useClientTableSort(
    preview?.units || [],
    unitSortAccessors,
    "unit_code"
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError("");
      onError?.("");
      const res = await getServiceChargeRecalculationPreview(propertyId);
      setPreview(res.data);
    } catch (e) {
      setPreview(null);
      const message = getApiErrorMessage(e, "Failed to load service charge preview.");
      setLoadError(message);
      onError?.(message);
    } finally {
      setLoading(false);
    }
  }, [propertyId, onError]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const openEdit = async (unit) => {
    setEditUnit(unit);
    setEditError("");
    setEditSquareFeet("");
    setEditRowVersion(null);
    setEditLoading(true);
    try {
      const data = await getUnitDetails(unit.unit_id);
      const detail = data?.unit || {};
      setEditSquareFeet(detail.square_feet != null ? String(detail.square_feet) : "");
      setEditRowVersion(detail.row_version ?? 1);
    } catch (e) {
      setEditError(getApiErrorMessage(e, "Failed to load unit details."));
    } finally {
      setEditLoading(false);
    }
  };

  const closeEdit = () => {
    if (editSaving) return;
    setEditUnit(null);
  };

  const handleEditSave = async () => {
    if (!editUnit) return;
    try {
      setEditSaving(true);
      setEditError("");
      await updateUnit(editUnit.unit_id, {
        square_feet: editSquareFeet === "" ? null : Number(editSquareFeet),
        row_version: editRowVersion,
      });
      setEditUnit(null);
      onMessage?.(`Square footage updated for ${editUnit.unit_name}.`);
      await load();
      onApplied?.();
    } catch (e) {
      if (isConcurrencyConflict(e)) {
        setEditError("This unit was changed elsewhere. Reopen to try again.");
      } else {
        setEditError(getApiErrorMessage(e, "Failed to update square footage."));
      }
    } finally {
      setEditSaving(false);
    }
  };

  const handleApply = async () => {
    try {
      setApplying(true);
      onError?.("");
      await applyServiceChargeRecalculation(propertyId);
      onMessage?.("Service charges updated for all units.");
      onApplied?.();
      await load();
    } catch (e) {
      onError?.(getApiErrorMessage(e, "Failed to apply service charge recalculation."));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Panel title="Service charge recalculation" noBodyPadding={false} sx={{ mb: 3 }}>
      {recalculationNeeded && (
        <Alert severity="warning" sx={{ mb: 2, whiteSpace: "pre-line" }}>
          {recalculationReason ||
            "Something material has changed about the budget or apportionments. Recalculate unit service charges."}
        </Alert>
      )}

      {loading ? (
        <Skeleton height={200} />
      ) : !preview ? (
        <Typography variant="body2" color="text.secondary">
          {loadError ||
            "Set a current fiscal period and create a budget to preview service charges."}
        </Typography>
      ) : (
        <>
          {preview.blocking_reason && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {preview.blocking_reason}
            </Alert>
          )}

          {(preview.apportionment_checks || []).some((row) => !row.within_tolerance) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {(preview.apportionment_checks || [])
                .filter((row) => !row.within_tolerance)
                .map((row) => (
                  <div key={row.section_id}>
                    {row.section_name}: {row.total_percentage?.toFixed?.(2) ?? row.total_percentage}%
                  </div>
                ))}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Review calculated service charges from the current fiscal period budget before updating
            every unit. Each unit&apos;s service charge is the sum of its Estate Charge share and
            assigned Block Charge shares. The sum of unit service charges may differ slightly from
            the budget total due to rounding — that is expected.
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
            <Chip label={`Budget: ${preview.budget?.name}`} size="small" />
            <Chip label={`FY: ${preview.fiscal_year?.label}`} size="small" />
            <Chip label={`Status: ${preview.budget?.status}`} size="small" />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
              gap: 2,
              mb: 3,
            }}
          >
            <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Budget total
              </Typography>
              <Typography variant="h6">{money(preview.budget_total)}</Typography>
            </Box>
            <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Sum of current service charges
              </Typography>
              <Typography variant="h6">
                {preview.first_time ? "—" : money(preview.current_total)}
              </Typography>
            </Box>
            <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Sum of new service charges
              </Typography>
              <Typography variant="h6">
                {money(preview.units_service_charge_total ?? preview.new_total)}
              </Typography>
              {!preview.first_time && preview.delta_total != null && (
                <Box sx={{ mt: 0.5 }}>
                  <DeltaCell delta={preview.delta_total} firstTime={false} />
                </Box>
              )}
            </Box>
            <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Rounding difference
              </Typography>
              <Typography variant="h6">{money(preview.rounding_difference)}</Typography>
            </Box>
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Charge sections
          </Typography>
          <TableContainer sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Section</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Current</TableCell>
                  <TableCell align="right">New</TableCell>
                  <TableCell align="right">Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(preview.sections || []).map((section) => (
                  <TableRow key={section.section_id}>
                    <TableCell>{section.section_name}</TableCell>
                    <TableCell>
                      {section.section_type === "estate" ? "Estate Charge" : "Block Charge"}
                    </TableCell>
                    <TableCell align="right">{money(section.current_total)}</TableCell>
                    <TableCell align="right">{money(section.new_total)}</TableCell>
                    <TableCell align="right">
                      <DeltaCell delta={section.delta} firstTime={preview.first_time} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Units
          </Typography>
          <TableContainer sx={{ maxHeight: FINANCE_UNITS_TABLE_MAX_HEIGHT, mb: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={sortField === "unit_code" ? sortDirection : false}>
                    <TableSortLabel
                      active={sortField === "unit_code"}
                      direction={sortField === "unit_code" ? sortDirection : "asc"}
                      onClick={() => handleSort("unit_code")}
                    >
                      Unit
                    </TableSortLabel>
                  </TableCell>
                  {preview.uses_square_footage && (
                    <TableCell align="right" sortDirection={sortField === "square_feet" ? sortDirection : false}>
                      <TableSortLabel
                        active={sortField === "square_feet"}
                        direction={sortField === "square_feet" ? sortDirection : "asc"}
                        onClick={() => handleSort("square_feet")}
                      >
                        Sq ft
                      </TableSortLabel>
                    </TableCell>
                  )}
                  <TableCell
                    align="right"
                    sortDirection={sortField === "estate_charge_amount" ? sortDirection : false}
                  >
                    <TableSortLabel
                      active={sortField === "estate_charge_amount"}
                      direction={sortField === "estate_charge_amount" ? sortDirection : "asc"}
                      onClick={() => handleSort("estate_charge_amount")}
                    >
                      Estate Charge
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="right"
                    sortDirection={sortField === "block_charge_amount" ? sortDirection : false}
                  >
                    <TableSortLabel
                      active={sortField === "block_charge_amount"}
                      direction={sortField === "block_charge_amount" ? sortDirection : "asc"}
                      onClick={() => handleSort("block_charge_amount")}
                    >
                      Block Charges
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="right"
                    sortDirection={sortField === "current_service_charge" ? sortDirection : false}
                  >
                    <TableSortLabel
                      active={sortField === "current_service_charge"}
                      direction={sortField === "current_service_charge" ? sortDirection : "asc"}
                      onClick={() => handleSort("current_service_charge")}
                    >
                      Current
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="right"
                    sortDirection={sortField === "new_service_charge" ? sortDirection : false}
                  >
                    <TableSortLabel
                      active={sortField === "new_service_charge"}
                      direction={sortField === "new_service_charge" ? sortDirection : "asc"}
                      onClick={() => handleSort("new_service_charge")}
                    >
                      New
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={sortField === "delta" ? sortDirection : false}>
                    <TableSortLabel
                      active={sortField === "delta"}
                      direction={sortField === "delta" ? sortDirection : "asc"}
                      onClick={() => handleSort("delta")}
                    >
                      Change
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedUnits.map((unit) => (
                  <TableRow key={unit.unit_id}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {unit.unit_name}
                        {unit.missing_square_footage && (
                          <Chip label="No sq ft" size="small" color="error" variant="outlined" />
                        )}
                        {preview.uses_square_footage && (
                          <Tooltip title="Edit square footage">
                            <IconButton
                              size="small"
                              onClick={() => openEdit(unit)}
                              aria-label={`Edit ${unit.unit_name}`}
                            >
                              <EditIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    {preview.uses_square_footage && (
                      <TableCell align="right">{unit.square_feet ?? "—"}</TableCell>
                    )}
                    <TableCell align="right">{money(unit.estate_charge_amount)}</TableCell>
                    <TableCell align="right">{money(unit.block_charge_amount)}</TableCell>
                    <TableCell align="right">{money(unit.current_service_charge)}</TableCell>
                    <TableCell align="right">{money(unit.new_service_charge)}</TableCell>
                    <TableCell align="right">
                      <DeltaCell delta={unit.delta} firstTime={unit.first_time} />
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ "& td": { fontWeight: 600, borderTop: 2, borderColor: "divider" } }}>
                  <TableCell>Total</TableCell>
                  {preview.uses_square_footage && <TableCell />}
                  <TableCell />
                  <TableCell />
                  <TableCell align="right">
                    {preview.first_time ? "—" : money(preview.current_total)}
                  </TableCell>
                  <TableCell align="right">
                    {money(preview.units_service_charge_total ?? preview.new_total)}
                  </TableCell>
                  <TableCell align="right">
                    <DeltaCell delta={preview.delta_total} firstTime={preview.first_time} />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Button
            variant="contained"
            disabled={applying || preview.can_apply === false}
            onClick={handleApply}
          >
            Apply service charges to units
          </Button>
        </>
      )}

      <Dialog open={Boolean(editUnit)} onClose={closeEdit} maxWidth="xs" fullWidth>
        <DialogTitle>Edit unit — {editUnit?.unit_name}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {editLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              {editError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {editError}
                </Alert>
              )}
              <TextField
                fullWidth
                label="Square feet"
                value={editSquareFeet}
                onChange={(e) => setEditSquareFeet(e.target.value.replace(/[^\d]/g, ""))}
                inputMode="numeric"
                disabled={editSaving}
                autoFocus
                helperText="Used to apportion the Estate Charge by floor area."
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={editSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditSave}
            disabled={editSaving || editLoading}
          >
            {editSaving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Panel>
  );
}
